import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Search, Ban, CheckCircle } from 'lucide-react';
import { productService } from '../../services/product-service';
import type { ProductAdminDto, ProductAdminQueryDto } from '../../types/admin-api';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';

const columnHelper = createColumnHelper<ProductAdminDto>();

export function ProductList() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('all');
  const [isActive, setIsActive] = useState<string>('all');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    productId: string;
    productTitle: string;
    action: 'enable' | 'disable';
  }>({
    isOpen: false,
    productId: '',
    productTitle: '',
    action: 'enable',
  });

  const query: ProductAdminQueryDto = {
    page,
    pageSize,
    search: search || undefined,
    status: status === 'all' ? undefined : (status as 'active' | 'disabled'),
    isActive: isActive === 'all' ? undefined : isActive === 'active',
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['products', query],
    queryFn: () => productService.getAll(query),
  });

  const setStatusMutation = useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      productService.setStatus(id, { isEnabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setConfirmDialog({ isOpen: false, productId: '', productTitle: '', action: 'enable' });
    },
  });

  const handleStatusChange = (product: ProductAdminDto, action: 'enable' | 'disable') => {
    setConfirmDialog({
      isOpen: true,
      productId: product.id,
      productTitle: product.title,
      action,
    });
  };

  const handleConfirmStatusChange = () => {
    const isEnabled = confirmDialog.action === 'enable';
    setStatusMutation.mutate({
      id: confirmDialog.productId,
      isEnabled,
    });
  };

  const columns = [
    columnHelper.accessor('title', {
      header: 'Product Title',
      cell: (info) => (
        <div>
          <div className="font-medium text-ink-primary">{info.getValue()}</div>
          {info.row.original.subtitle && (
            <div className="text-sm text-ink-muted">{info.row.original.subtitle}</div>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('businessName', {
      header: 'Business',
      cell: (info) => <span className="text-ink-secondary">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('price', {
      header: 'Price',
      cell: (info) => (
        <span className="text-ink-secondary">
          PLN {info.getValue().toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
        </span>
      ),
    }),
    columnHelper.accessor('usage', {
      header: 'Usage',
      cell: (info) => <span className="text-ink-secondary">{info.getValue()}</span>,
    }),
    columnHelper.accessor('startDate', {
      header: 'Start Date',
      cell: (info) => (
        <span className="text-ink-secondary">
          {new Date(info.getValue()).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </span>
      ),
    }),
    columnHelper.accessor('endDate', {
      header: 'End Date',
      cell: (info) => (
        <span className="text-ink-secondary">
          {new Date(info.getValue()).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </span>
      ),
    }),
    columnHelper.accessor('isActive', {
      header: 'Active',
      cell: (info) => (
        <StatusBadge isEnabled={info.getValue()} activeText="Active" disabledText="Inactive" />
      ),
    }),
    columnHelper.accessor('isEnabled', {
      header: 'Status',
      cell: (info) => <StatusBadge isEnabled={info.getValue()} />,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center gap-2">
          {info.row.original.isEnabled ? (
            <button
              onClick={() => handleStatusChange(info.row.original, 'disable')}
              className="p-1 text-brand-warm hover:bg-brand-warm/10 rounded"
              title="Disable"
            >
              <Ban size={18} />
            </button>
          ) : (
            <button
              onClick={() => handleStatusChange(info.row.original, 'enable')}
              className="p-1 text-brand-confirm hover:bg-brand-confirm/10 rounded"
              title="Enable"
            >
              <CheckCircle size={18} />
            </button>
          )}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (error) {
    return (
      <div className="bg-brand-warm/20 border border-brand-warm/40 text-brand-warm px-4 py-3 rounded">
        Error loading products: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Filters */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <h2 className="font-display text-xl font-semibold tracking-tight text-ink-primary mb-4">All Products</h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-subtle"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by title..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="active">Enabled Only</option>
            <option value="disabled">Disabled Only</option>
          </select>

          {/* Active Filter */}
          <select
            value={isActive}
            onChange={(e) => {
              setIsActive(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            <option value="all">All Products</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>

          {/* Clear Filters */}
          {(search || status !== 'all' || isActive !== 'all') && (
            <button
              onClick={() => {
                setSearch('');
                setStatus('all');
                setIsActive('all');
                setPage(1);
              }}
              className="px-4 py-2 text-white bg-white/10 border border-white/30 rounded-lg hover:bg-white/20"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-ink-muted">Loading products...</div>
        ) : data?.data && data.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-6 py-3 text-left text-xs font-medium text-ink-secondary uppercase tracking-wider"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-white/10">
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="hover:bg-white/5">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.pagination && data.pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
                <div className="text-sm text-ink-secondary">
                  Showing page {data.pagination.page} of {data.pagination.totalPages} (
                  {data.pagination.totalItems} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= data.pagination.totalPages}
                    className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-ink-muted">No products found</div>
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => {
          setConfirmDialog({ isOpen: false, productId: '', productTitle: '', action: 'enable' });
        }}
        onConfirm={handleConfirmStatusChange}
        title={confirmDialog.action === 'enable' ? 'Enable Product' : 'Disable Product'}
        message={`Are you sure you want to ${confirmDialog.action} "${confirmDialog.productTitle}"?`}
        confirmText={confirmDialog.action === 'enable' ? 'Enable' : 'Disable'}
        confirmVariant={confirmDialog.action === 'disable' ? 'danger' : 'primary'}
        isLoading={setStatusMutation.isPending}
      />
    </div>
  );
}
